import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["name"]
const _hoisted_2 = ["name", "value"]

import list from "./list.vue";
import { getProductCollectionTree as webApiGetProductCollectionTree } from "./web-api";
import {
    flattenAllCollectionsWithParents,
    flattenAllCollections,
} from "./collection-methods";
// import CoolLoader from "../../shared/CoolLoader.vue";
// import FadeAndSlide from "../../shared/FadeAndSlide.vue";
import {
    type PropType,
    onMounted,
    ref,
    useCssModule,
    onBeforeUnmount,
    computed,
    nextTick,
    watch,
} from "vue";
import {
    type CollectionFilter,
    type PreselectedProductCollectionGroup,
    type ProductCollectionLeveledStructure,
} from "../types";
import { type MappedProductCollectionModel } from "./models";
import { constructMappedItem } from "./view-modes/mapped-items";
import type { UrlResolver } from "../seo-url-parser";
import { createUrlParsers } from "../url-parsers";


export default /*@__PURE__*/_defineComponent({
  __name: 'collection-filter',
  props: {
    productListGuid: Number,
    matchedLogicalName: {
        type: String,
    },
    matchedLogicalNameEntityGuid: Number,
    matchedLogicalNameModuleGuid: Number,
    matchedLogicalNameSubModuleGuid: Number,
    model: Object as PropType<CollectionFilter>,
    urlResolver: Object as PropType<UrlResolver>,
    submit: Function,
    initializeAwaiters: Array as PropType<Promise<void>[]>,
    formIsLoading: Boolean,
    value: Array as PropType<number[][] | undefined>,
    getProductCollectionTree: {
        type: Function as PropType<typeof webApiGetProductCollectionTree>,
    },
},
  emits: ["input", "update:viewMode"],
  setup(__props, { expose: __expose, emit: __emit }) {

function createFillerRoot(
    cnt: number,
    depth = 0,
    ctx: { counter: number } | null = null
): MappedProductCollectionModel[] {
    const result: Partial<MappedProductCollectionModel>[] = [];

    if (ctx == null) ctx = { counter: 0 };

    for (let i = 0; i < cnt; i++) {
        result.push({
            Id: ctx.counter++,
            SubCollections:
                depth > 0
                    ? []
                    : (createFillerRoot(
                          17,
                          depth + 1,
                          ctx
                      ) as MappedProductCollectionModel[]),
            Visible: true,
            Filler: true,
        });
    }

    return result as unknown as MappedProductCollectionModel[];
}

const props = __props;

const updateGroupsList_loading = ref(false);
const updateCounts_loading = ref(false);
const manualCollectionIdGroups = ref<number[][]>();

const urlParserWrapper = createUrlParsers().collections;

const root = urlParserWrapper.state.root;

const fillerRoot = ref(createFillerRoot(4));

const manualScopedRoot = urlParserWrapper.state.manualScopedRoot;
const selectedScopedRoot = ref<MappedProductCollectionModel>();

const rootTask = ref<Promise<void>>();
const includeTest = ref(false);
const localViewMode = ref(undefined);
const currentStructure = ref<ProductCollectionLeveledStructure>();
const overwrittenVisibilities = ref<number[]>([]);

const scopedRoot = computed(() => {
    return manualScopedRoot.value ?? selectedScopedRoot.value;
});

const collectionIdGroups = computed<number[][] | undefined>(() => {
    if (
        manualCollectionIdGroups.value != null &&
        manualCollectionIdGroups.value.length > 0 &&
        manualCollectionIdGroups.value.some((x) => x.length > 0)
    ) {
        return manualCollectionIdGroups.value;
    }
    if (selectedScopedRoot.value != null) {
        return [[selectedScopedRoot.value.Id]];
    }
    return undefined;
});

const emit = __emit;

watch(
    () => collectionIdGroups.value,
    (newValue) => {
        emit("input", newValue);
    }
);

const $style = useCssModule();

// const classNames = ref({
//     groupName: $style["group-name"],
//     currentGroupName: $style["current-group-name"],
//     unorderedList: $style["unordered-list"],
//     hasChildren: $style["has-children"],
//     subTree: $style["sub-tree"],
//     underlined: $style["underlined"],
// });

async function updateGroupsList(
    preselectedCollectionIds: number[][] | undefined
) {
    updateGroupsList_loading.value = true;
    const { collections, currentStructure: currentStruct } = await getGroupList(
        preselectedCollectionIds,
        currentStructure.value
    );
    root.value = collections.map((x) => constructMappedItem(x, undefined));
    currentStructure.value = currentStruct;
    updateGroupsList_loading.value = false;
}

const lastRequestedData =
    ref<Parameters<typeof webApiGetProductCollectionTree>>();

async function getGroupList(
    preselectedCollectionIds: number[][] | undefined,
    structure: ProductCollectionLeveledStructure | null | undefined
) {
    const args: Parameters<typeof webApiGetProductCollectionTree> = [
        props.productListGuid,
        constructCollectionIdGroupModelItems(preselectedCollectionIds),
        structure,
        includeTest.value,
    ];
    const result = await (
        props.getProductCollectionTree ?? webApiGetProductCollectionTree
    )(...args);
    lastRequestedData.value = args;
    return result;
}
function constructCollectionIdGroupModelItems(
    collIdGroups: number[][] | undefined | null
): PreselectedProductCollectionGroup[] {
    if (collIdGroups == null) return [];
    const result: PreselectedProductCollectionGroup[] = [];

    const mappedWithParents = Array.from(
        flattenAllCollectionsWithParents(root.value)
    );

    for (const collectionIdGroup of collIdGroups) {
        const group: PreselectedProductCollectionGroup = {
            ParentCollectionGroupId: 0,
            ProductCollectionGroups: [],
        };
        for (const collectionId of collectionIdGroup) {
            if (group.ParentCollectionGroupId == null) {
                const match = mappedWithParents.find(
                    (set) => set.collection.Id == collectionId
                );
                if (match != null) {
                    group.ParentCollectionGroupId = match.parents[0].Id;
                }
            }
            group.ProductCollectionGroups.push(collectionId);
        }
        if (
            group.ParentCollectionGroupId != null &&
            group.ProductCollectionGroups.length > 0
        ) {
            result.push(group);
        }
    }
    return result;
}

// const currentCollectionModel = computed(() => {
//     return undefined as ProductCollectionModel | undefined;
//     // if (currentStructure.value == null || currentStructure.value.Ids.length !== 1)
//     //     return undefined;
//     // const id = currentStructure.value.Ids[0];
//     // return getCollectionModelById(id);
// });

function getCollectionModelById(id: number) {
    for (const col of flattenAllCollections(root.value)) {
        if (col.Id == id) {
            return col;
        }
    }
    return undefined;
}

async function updateCounts() {
    updateCounts_loading.value = true;
    // console.debug("UPDATING PRODUCT COUNTS");
    const { collections } = await getGroupList(
        manualCollectionIdGroups.value,
        currentStructure.value
    );
    const newCounts = Array.from(flattenAllCollections(collections));
    for (const collection of flattenAllCollections(root.value)) {
        const match = newCounts.find((col) => col.Id == collection.Id);
        if (match != null) collection.ProductCount = match.ProductCount;
        else collection.ProductCount = undefined;
    }
    updateCounts_loading.value = false;
}
function collectionGroupIndexer(
    collIdGroups: number[][],
    collectionIdGroup: number[]
) {
    return `${props.model?.collectionIdName}[${collIdGroups.indexOf(
        collectionIdGroup
    )}]`;
}
function emptyIndexerName(
    collIdGroups: number[][],
    collectionIdGroup: number[]
) {
    return `${collectionGroupIndexer(collIdGroups, collectionIdGroup)}`;
}
function indexerName(
    collIdGroups: number[][],
    collectionIdGroup: number[],
    collectionId: number
) {
    return `${collectionGroupIndexer(
        collIdGroups,
        collectionIdGroup
    )}[${collectionIdGroup.indexOf(collectionId)}]`;
}
function isVisible(item: { Visible: boolean; Id: number }) {
    if (item.Visible == true) return true;
    if (overwrittenVisibilities.value.indexOf(item.Id) !== -1) return true;
    return false;
}

const element = ref<HTMLDivElement>();

function findForm() {
    if (element.value == null) return null;
    let el = element.value.parentElement;
    while (true) {
        if (el == null) break;
        if (el.tagName.toLowerCase() === "form") return el;
        if (el.parentElement == null) break;
        el = el.parentElement;
    }
    return null;
}
function resetFilterCall() {
    // postFormChange(null, true);
}

function tryScopeToCollection(collIdGroups: number[][] | null | undefined) {
    // console.debug("trying to scope");
    //debugger;
    if (collIdGroups == null) return undefined;
    if (collIdGroups.length == 0) return undefined;
    if (collIdGroups.length != 1) {
        collIdGroups = collIdGroups.filter((x) => x.length != 0);
    }
    if (collIdGroups.length !== 1) return undefined;
    if (collIdGroups[0].length != 1) return undefined;
    if (actualViewMode.value != 0) return undefined;
    let model = getCollectionModelById(collIdGroups[0][0]);
    if (model == null) return undefined;
    if (model.FilterViewMode !== 1) {

        while (model != null) {
            const mod : MappedProductCollectionModel = model;
            const mappedModel = mappedFlatRoot.value.find(
                (x) => x.Id == mod.Id
            );

            if (mappedModel?.parent?.FilterViewMode === 1) {
                return {
                    scopedRoot: mappedModel.parent,
                    newGroupValue: [[model.Id]],
                };
            }

            if (mappedModel?.parent?.parent?.FilterViewMode === 1) {
                selectedScopedRoot.value = mappedModel.parent.parent;
                return undefined;
            }

            if (mappedModel?.parent != null) {
                model = mappedModel.parent;
                continue;
            }
            break;
        }
        return undefined;
    }

    selectedScopedRoot.value = model;
    return {
        scopedRoot: model,
        newGroupValue: [],
    };
}

const mappedFlatRoot = computed(() => {
    return Array.from(flattenAllCollections(root.value));
});

function tryUnscopeToCollection(collIdGroups: number[][] | null | undefined) {
    // console.debug("unscope");
    if (selectedScopedRoot.value == null) return false;

    const selScopeRoot = selectedScopedRoot.value;

    if (collIdGroups != null) return false;

    const mappedSelectedScopeRoot = mappedFlatRoot.value.find(
        (x) => x.Id == selScopeRoot.Id
    );

    if (mappedSelectedScopeRoot == null) return false;

    if (mappedSelectedScopeRoot.parent == null) {
        selectedScopedRoot.value = undefined;
    } else {
        throw new Error("This case is not implemented");
    }
    // else if (
    //     mappedSelectedScopeRoot.parent != null &&
    //     mappedSelectedScopeRoot.parent.Id == collIdGroups[0][0]
    // ) {
    //     selectedScopedRoot.value = mappedSelectedScopeRoot.parent;
    //     return true;
    // }

    // if (model == null) return false;
    // if (model.FilterViewMode !== 1) return false;

    return false;
}

async function postFormChange(
    collIdGroups: number[][] | undefined | null,
    doNotTrigger?: boolean,
    scopedRoot?: MappedProductCollectionModel
) {
    // console.debug(
    //     "postFormChange",
    //     collIdGroups,
    //     "submit:" + (doNotTrigger !== true).toString()
    // );

    if (scopedRoot != null) {
        selectedScopedRoot.value = scopedRoot;
        manualCollectionIdGroups.value = collIdGroups ?? undefined;
    } else {
        const scopeResult = tryScopeToCollection(collIdGroups);
        if (scopeResult != null) {
            manualCollectionIdGroups.value = scopeResult.newGroupValue;
            selectedScopedRoot.value = scopeResult.scopedRoot;
        } else if (tryUnscopeToCollection(collIdGroups)) {
        } else if (manualCollectionIdGroups.value != collIdGroups) {
            manualCollectionIdGroups.value = collIdGroups ?? undefined;
        }
    }
    // selectedGroupChange(group, doNotTrigger);

    if (doNotTrigger !== true) {
        return new Promise((res) => {
            nextTick(function () {
                if (props.submit != null) props.submit();
                res(void 0);
            });
        });
    } else {
        // console.debug("update counts from postFormChange without triggering");
        updateCounts();
        return new Promise((res) => {
            nextTick(function () {
                res(void 0);
            });
        });
    }
}
// function setParents(group: { parent: any; children: any }, parent: null) {
//     group.parent = parent;
//     for (const child of group.children) {
//         setParents(child, group);
//     }
// }
// function makeKey(group: { guid: string; parent: null }) {
//     let key = "" + group.guid;
//     if (group.parent != null) {
//         key = makeKey(group.parent) + "_" + key;
//     }
//     return key;
// }
// function match(a, b) {
//     return a != null && b != null && a === b;
// }
// function owns(a, b, limit = -1) {
//     if (b == null) return false;

//     if (match(a, b)) return true;

//     if (limit === 0) return false;

//     if (limit > 0) limit = limit - 1;

//     return owns(a, b.parent, limit);
// }
// function findGroupById(groupId: number) {
//     return findGroupByMethod((g: { guid: any }) => g.guid === groupId);
// }
// function findGroupByMethod(method: (g: any) => boolean) {
//     return findGroupsByMethod(method, 1)[0];
// }

// const groups = ref<never[]>();

// function findGroupsByMethod(
//     method: (arg0: any) => boolean,
//     maxCount = 0,
//     result = [],
//     groups = null,
//     alreadyChecked = []
// ) {
//     console.warn("findGroupsByMethod");
//     return result;
//     // if (groups === null) {
//     //     groups = groups.value;
//     // }
//     // if (maxCount > 0 && result.length >= maxCount) return result;
//     // for (const group of groups) {
//     //     if (maxCount > 0 && result.length >= maxCount) break;

//     //     if (alreadyChecked.indexOf(group) !== -1) continue;
//     //     alreadyChecked.push(group);

//     //     if (method(group) === true) {
//     //         result.push(group);
//     //     }

//     //     if (Array.isArray(group.children) && group.children.length > 0) {
//     //         findGroupsByMethod(
//     //             method,
//     //             maxCount,
//     //             result,
//     //             group.children,
//     //             alreadyChecked
//     //         );
//     //     }
//     // }
//     // return result;
// }
// function resolveUrl() {
//     console.warn("RESOLVEURL");
//     return;
//     // const currentUrl = location.pathname.toLowerCase();
//     // if (currentUrl.indexOf(props.matchedLogicalName.toLowerCase()) !== 0)
//     //     throw new Error("Could not resolve group path of url: " + currentUrl);
//     // let subPath = currentUrl.substring(props.matchedLogicalName.length);

//     // let group = null;
//     // let groups = groups.value;

//     // if (subPath.length > 0) {
//     //     while (subPath.length > 0) {
//     //         if (subPath.indexOf("/") !== 0)
//     //             throw new Error("could not parse url. missing /");
//     //         let part = subPath;
//     //         if (part.indexOf("/", 1) !== -1) {
//     //             part = part.substring(0, part.indexOf("/", 1));
//     //         }

//     //         const matches = groups.filter(
//     //             (x: { subLogicalName: string }) =>
//     //                 x.subLogicalName.toLowerCase() === part
//     //         );
//     //         if (matches.length === 0) throw new Error("could not find match");

//     //         group = matches[0];
//     //         groups = group.children;

//     //         subPath = subPath.substring(part.length);
//     //     }
//     // } else if (location.search != null && location.search.length > 1) {
//     //     const URLSearchParams = window.URLSearchParams;
//     //     const searchParams = new URLSearchParams(location.search);
//     //     let groupIds = searchParams.getAll("groups");

//     //     if (groupIds.length === 0) {
//     //         groupIds = searchParams.getAll("gcgroups");
//     //     }

//     //     if (groupIds.length > 0) {
//     //         const groupId = parseInt(groupIds[0]);
//     //         group = findGroupById(groupId);
//     //     }
//     // }

//     // postFormChange(group, true);
// }

// function hasVisibleChildren(group) {
//     return (
//         Array.isArray(group.children) &&
//         group.children.filter((m) => m.invisible !== true).length > 0
//     );
// }
// function hasNoVisibleChildren(group) {
//     return (
//         !Array.isArray(group.children) ||
//         group.children.filter((m) => m.invisible !== true).length === 0
//     );
// }

//import { makeGroupedValue as makeGroupedValueF } from "./value";

// function makeGroupedValue(
//     id: number,
//     existingValue: number[][] | undefined = undefined,
//     viewMode?: number
// ): { value: number[][]; scopedRootId?: number } {
//     if (viewMode == null) viewMode = actualViewMode.value;
//     return makeGroupedValueF(id, existingValue, viewMode, root.value);
// }

// watch(
//     () => groups.value,
//     (newValue: any) => {
//         if (!Array.isArray(newValue)) return;
//         for (const group of newValue) {
//             setParents(group, null);
//         }
//     },
//     { immediate: true }
// );

onMounted(async () => {
    const form = findForm();
    if (form != null) {
        const $form = window.$(form);
        let events = $form.data("resetEvents");
        if (!Array.isArray(events)) {
            events = [];
            $form.data("resetEvents", events);
        }
        events.push({
            handler: () => {
                resetFilterCall();
            },
            item: this,
        });
    } else {
        console.warn("no form found");
    }

    await rootTask.value;

    // console.debug("MOUNTED", manualCollectionIdGroups.value);
    //resolveUrl();
});

onBeforeUnmount(() => {
    const form = findForm();
    if (form != null) {
        const $form = window.$(form);
        const events = $form.data("resetEvents");
        if (Array.isArray(events)) {
            const removal = events.filter((e) => e.item === this);

            for (const item of removal) {
                events.splice(events.indexOf(item), 1);
            }
        }
    } else {
        console.warn("no form found");
    }
});

const initTask = (rootTask.value = (async () => {
    const URLSearchParams = window.URLSearchParams;
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get("showTestProducts") === "true") {
        includeTest.value = true;
    }
    await updateGroupsList(undefined);
})());

props.initializeAwaiters?.push(initTask);

urlParserWrapper.postFormChangeHandlers.push(async (colId) => {
    await postFormChange(colId, true);
});

const urlParser = urlParserWrapper.parser;

watch(
    () => props.urlResolver,
    (newValue, oldValue) => {
        if (oldValue == null && newValue != null) {
            newValue.addParser(urlParser);
        }
    },
    {
        immediate: true,
    }
);

__expose({
    postFormChange,
});

// const currentSelectedIsEmptyAndAtRoot = computed(() => {
//     return (
//         this.selectedGroup != null &&
//         this.selectedGroup.parent == null &&
//         hasNoVisibleChildren(this.selectedGroup)
//     );
// });
const isLoading = computed(() => {
    return updateGroupsList_loading.value || updateCounts_loading.value;
});
const actualViewMode = computed(() => {
    if (scopedRoot.value != null && scopedRoot.value.FilterViewMode != null) {
        return scopedRoot.value.FilterViewMode;
    }
    if (localViewMode.value != null) return localViewMode.value;

    return props.model?.viewMode ?? 0;
});

watch(
    () => actualViewMode.value,
    (newValue) => {
        urlParserWrapper.state.actualViewMode.value = newValue;
    },
    {
        immediate: true,
    }
);

watch(
    () => actualViewMode.value,
    (newValue) => {
        emit("update:viewMode", newValue);
    },
    {
        immediate: true,
    }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([
            'product-collection-filter-component',
            !__props.formIsLoading && isLoading.value ? _unref($style)['loading'] : '',
        ]),
    ref_key: "element",
    ref: element
  }, [
    (collectionIdGroups.value != null)
      ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(collectionIdGroups.value, (collectionIdGroup) => {
          return (_openBlock(), _createElementBlock(_Fragment, null, [
            (collectionIdGroup.length == 0)
              ? (_openBlock(), _createElementBlock("input", {
                  key: 0,
                  type: "hidden",
                  name: 
                            emptyIndexerName(
                                collectionIdGroups.value,
                                collectionIdGroup
                            )
                        ,
                  value: 0
                }, null, 8 /* PROPS */, _hoisted_1))
              : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(collectionIdGroup, (collectionId) => {
                  return (_openBlock(), _createElementBlock("input", {
                    type: "hidden",
                    name: 
                                indexerName(
                                    collectionIdGroups.value,
                                    collectionIdGroup,
                                    collectionId
                                )
                            ,
                    value: collectionId
                  }, null, 8 /* PROPS */, _hoisted_2))
                }), 256 /* UNKEYED_FRAGMENT */))
          ], 64 /* STABLE_FRAGMENT */))
        }), 256 /* UNKEYED_FRAGMENT */))
      : _createCommentVNode("v-if", true),
    _createCommentVNode(" <fade-and-slide>\r\n            <cool-loader\r\n                v-if=\"root == null && !mobile\"\r\n                :absolute=\"true\"\r\n                :size=\"40\"\r\n                :line-length=\"10\"\r\n                :thickness=\"3\"\r\n                vertical-center\r\n            >\r\n                <cool-loader></cool-loader>\r\n            </cool-loader>\r\n        </fade-and-slide> "),
    (_unref(root) == null)
      ? (_openBlock(), _createBlock(list, {
          key: "fakeList",
          filler: true,
          "get-visible": isVisible,
          "view-mode": actualViewMode.value,
          root: fillerRoot.value,
          "base-url": __props.matchedLogicalName
        }, null, 8 /* PROPS */, ["view-mode", "root", "base-url"]))
      : (_openBlock(), _createBlock(list, {
          ref: "folderNavigator",
          key: "collectionList",
          "force-back-arrow": scopedRoot.value === selectedScopedRoot.value,
          "get-visible": isVisible,
          "view-mode": actualViewMode.value,
          value: manualCollectionIdGroups.value,
          onInput: _cache[0] || (_cache[0] = (v) => postFormChange(v.value, undefined, v.scopedRoot)),
          root: _unref(root),
          "scoped-root": scopedRoot.value,
          "base-url": __props.matchedLogicalName,
          "matched-logical-name": __props.matchedLogicalName,
          "matched-logical-name-module-guid": __props.matchedLogicalNameModuleGuid,
          "matched-logical-name-sub-module-guid": 
                __props.matchedLogicalNameSubModuleGuid
            ,
          "matched-logical-name-entity-guid": __props.matchedLogicalNameEntityGuid,
          "product-list-guid": __props.productListGuid,
          onUpdateProductCount: _cache[1] || (_cache[1] = ($event: any) => (updateCounts()))
        }, null, 8 /* PROPS */, ["force-back-arrow", "view-mode", "value", "root", "scoped-root", "base-url", "matched-logical-name", "matched-logical-name-module-guid", "matched-logical-name-sub-module-guid", "matched-logical-name-entity-guid", "product-list-guid"]))
  ], 2 /* CLASS */))
}
}

})